import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Search, 
  Plus, 
  Upload, 
  Download,
  Edit,
  Eye,
  FileText,
  Users,
  Calendar,
  MoreVertical,
  Award,
  Briefcase,
  Globe,
  Phone,
  Mail,
  MapPin,
  X,
  Check,
  Lock,
  Unlock,
  Shield,
  UserPlus,
  Settings,
  Star,
  Bell,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl as getApiBaseUrlUtil } from '../utils/api';

interface Company {
  id: string;
  name: string;
  type: string;
  industry: string;
  ceo: string;
  employees: number;
  revenue: string;
  established: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  description: string;
  portfolioCount: number;
  matchedGrants: number;
  successRate: number;
  tags: string[];
  createdAt: string;
  visibility: 'public' | 'members' | 'partner_only' | 'private';
  registrationStatus: 'pending' | 'approved' | 'rejected';
  isMyCompany?: boolean;
}

interface Member {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  is_accepted: boolean;
  invited_at: string;
  accepted_at?: string;
}

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return `${protocol}//${hostname}:8001`;
};

const CompanyDataPool: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [activeTab, setActiveTab] = useState<'all' | 'my'>('all');

  const companyTypes = [
    { value: 'all', label: '전체 유형' },
    { value: '대기업', label: '대기업' },
    { value: '중견기업', label: '중견기업' },
    { value: '중소기업', label: '중소기업' },
    { value: '스타트업', label: '스타트업' },
    { value: '비영리기관', label: '비영리기관' },
    { value: '연구기관', label: '연구기관' },
    { value: '협회', label: '협회' },
  ];

  const industries = [
    { value: 'all', label: '전체 산업' },
    { value: 'IT', label: 'IT/소프트웨어' },
    { value: 'Manufacturing', label: '제조업' },
    { value: 'Bio', label: '바이오/헬스케어' },
    { value: 'Energy', label: '에너지/환경' },
    { value: 'Contents', label: '콘텐츠/미디어' },
    { value: 'Finance', label: '금융/핀테크' },
    { value: 'Agriculture', label: '농업/식품' },
  ];

  const visibilityOptions = [
    { value: 'public', label: '전체 공개', icon: Globe, desc: '모든 사용자에게 공개' },
    { value: 'members', label: '회원 공개', icon: Users, desc: '로그인 사용자에게만 공개' },
    { value: 'partner_only', label: '파트너 전용', icon: Shield, desc: '승인된 파트너에게만 공개' },
    { value: 'private', label: '비공개', icon: Lock, desc: '담당자 및 관리자만 조회' },
  ];

  const roleLabels: Record<string, string> = {
    owner: '소유자',
    admin: '관리자',
    editor: '편집자',
    viewer: '뷰어',
  };

  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const baseUrl = getApiBaseUrlUtil();

        const response = await fetch(`${baseUrl}/api/companies/?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setCompanies(data);
            return;
          }
          if (data.companies && data.companies.length > 0) {
            setCompanies(data.companies);
            return;
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return; // Cleanup, ignore
        console.error('Failed to fetch companies:', err);
      }
    };

    fetchCompanies();
    return () => controller.abort();
  }, []);

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         company.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         company.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = selectedType === 'all' || company.type === selectedType;
    const matchesIndustry = selectedIndustry === 'all' || company.industry === selectedIndustry;
    const matchesTab = activeTab === 'all' || company.isMyCompany;
    
    return matchesSearch && matchesType && matchesIndustry && matchesTab;
  });

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      '대기업': 'bg-red-100 text-red-800',
      '중견기업': 'bg-orange-100 text-orange-800',
      '중소기업': 'bg-blue-100 text-blue-800',
      '스타트업': 'bg-purple-100 text-purple-800',
      '비영리기관': 'bg-green-100 text-green-800',
      '연구기관': 'bg-indigo-100 text-indigo-800',
      '협회': 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public': return <Globe className="h-4 w-4 text-green-600" />;
      case 'members': return <Users className="h-4 w-4 text-blue-600" />;
      case 'partner_only': return <Shield className="h-4 w-4 text-orange-600" />;
      case 'private': return <Lock className="h-4 w-4 text-gray-600" />;
      default: return <Globe className="h-4 w-4" />;
    }
  };

  const stats = {
    total: companies.length,
    totalPortfolios: companies.reduce((sum, c) => sum + c.portfolioCount, 0),
    avgSuccessRate: companies.length > 0
      ? Math.round(companies.reduce((sum, c) => sum + c.successRate, 0) / companies.length)
      : 0,
    totalMatched: companies.reduce((sum, c) => sum + c.matchedGrants, 0),
    myCompanies: companies.filter(c => c.isMyCompany).length,
  };

  const loadMembers = async (companyId: string) => {
    // Mock 데이터
    setMembers([
      { id: '1', user_id: '1', user_email: 'admin@techsolution.kr', user_name: '김관리자', role: 'owner', is_accepted: true, invited_at: '2024-01-01T00:00:00Z', accepted_at: '2024-01-01T00:00:00Z' },
      { id: '2', user_id: '2', user_email: 'user@techsolution.kr', user_name: '이담당', role: 'editor', is_accepted: true, invited_at: '2024-02-01T00:00:00Z', accepted_at: '2024-02-02T00:00:00Z' },
      { id: '3', user_id: '', user_email: 'pending@example.com', user_name: 'pending@example.com', role: 'viewer', is_accepted: false, invited_at: '2024-03-01T00:00:00Z' },
    ]);
  };

  const handleVisibilityChange = async (companyId: string, visibility: string) => {
    // API 호출 (실제 구현 시)
    setCompanies(prev => prev.map(c => 
      c.id === companyId ? { ...c, visibility: visibility as Company['visibility'] } : c
    ));
    if (selectedCompany?.id === companyId) {
      setSelectedCompany(prev => prev ? { ...prev, visibility: visibility as Company['visibility'] } : null);
    }
    setShowVisibilityModal(false);
  };

  const handleInviteMember = async () => {
    if (!inviteEmail || !selectedCompany) return;
    
    // Mock: 새 멤버 추가
    const newMember: Member = {
      id: String(Date.now()),
      user_id: '',
      user_email: inviteEmail,
      user_name: inviteEmail.split('@')[0],
      role: inviteRole,
      is_accepted: false,
      invited_at: new Date().toISOString(),
    };
    setMembers(prev => [...prev, newMember]);
    setInviteEmail('');
    setInviteRole('viewer');
  };

  const handleRemoveMember = async (memberId: string) => {
    setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const openMemberModal = (company: Company) => {
    setSelectedCompany(company);
    loadMembers(company.id);
    setShowMemberModal(true);
  };

  const openVisibilityModal = (company: Company) => {
    setSelectedCompany(company);
    setShowVisibilityModal(true);
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            기업/기관 데이터풀
          </h1>
          <p className="text-gray-600 mt-2">등록된 기업/기관의 포트폴리오와 이력을 관리합니다</p>
        </div>
        <div className="flex space-x-3">
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <Upload className="h-5 w-5 mr-2" />
            일괄 업로드
          </button>
          <button 
            onClick={() => navigate('/company-registration')}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            기업 등록
          </button>
        </div>
      </div>

      {/* 탭 */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          전체 기업 ({stats.total})
        </button>
        <button
          onClick={() => setActiveTab('my')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'my'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          내 기업 ({stats.myCompanies})
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">등록 기업/기관</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Building2 className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">총 포트폴리오</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalPortfolios}</p>
            </div>
            <FileText className="h-8 w-8 text-purple-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">평균 선정률</p>
              <p className="text-2xl font-bold text-green-600">{stats.avgSuccessRate}%</p>
            </div>
            <Award className="h-8 w-8 text-green-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">매칭된 공모사업</p>
              <p className="text-2xl font-bold text-orange-600">{stats.totalMatched}</p>
            </div>
            <Briefcase className="h-8 w-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="기업명, 키워드 검색..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-full"
          />
        </div>
        
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        >
          {companyTypes.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        <select
          value={selectedIndustry}
          onChange={(e) => setSelectedIndustry(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        >
          {industries.map((ind) => (
            <option key={ind.value} value={ind.value}>{ind.label}</option>
          ))}
        </select>
      </div>

      {/* 기업 목록 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCompanies.map((company) => (
          <div key={company.id} className="card hover:shadow-medium transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                  {company.name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {company.name}
                    </h3>
                    {company.isMyCompany && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                        내 기업
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeColor(company.type)}`}>
                      {company.type}
                    </span>
                    <span className="text-sm text-gray-500">{company.industry}</span>
                    <span className="flex items-center" title={`공개 설정: ${visibilityOptions.find(v => v.value === company.visibility)?.label}`}>
                      {getVisibilityIcon(company.visibility)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* 액션 드롭다운 */}
              {company.isMyCompany && (
                <div className="flex space-x-1">
                  <button 
                    onClick={() => openVisibilityModal(company)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="공개 설정"
                  >
                    <Settings className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => openMemberModal(company)}
                    className="p-2 text-gray-400 hover:text-emerald-600 transition-colors"
                    title="담당자 관리"
                  >
                    <Users className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{company.description}</p>

            {/* 통계 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900">{company.portfolioCount}</p>
                <p className="text-xs text-gray-500">포트폴리오</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-600">{company.matchedGrants}</p>
                <p className="text-xs text-gray-500">매칭 공모</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-blue-600">{company.successRate}%</p>
                <p className="text-xs text-gray-500">선정률</p>
              </div>
            </div>

            {/* 기본 정보 */}
            <div className="space-y-2 mb-4 text-sm">
              <div className="flex items-center space-x-2 text-gray-600">
                <Users className="h-4 w-4" />
                <span>직원 {company.employees}명</span>
                <span className="text-gray-300">|</span>
                <span>매출 {company.revenue}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <MapPin className="h-4 w-4" />
                <span className="truncate">{company.address}</span>
              </div>
            </div>

            {/* 태그 */}
            <div className="flex flex-wrap gap-2 mb-4">
              {company.tags.slice(0, 4).map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                  {tag}
                </span>
              ))}
              {company.tags.length > 4 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md">
                  +{company.tags.length - 4}
                </span>
              )}
            </div>

            {/* 액션 버튼 */}
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setSelectedCompany(company)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Eye className="h-4 w-4 mr-2" />
                상세보기
              </button>
              <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                <Edit className="h-4 w-4" />
              </button>
              <button className="p-2 text-gray-400 hover:text-emerald-600 transition-colors">
                <Download className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 빈 상태 */}
      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {activeTab === 'my' ? '등록된 내 기업이 없습니다' : '등록된 기업/기관이 없습니다'}
          </h3>
          <p className="text-gray-600 mb-4">새로운 기업을 등록하거나 검색 조건을 변경해보세요</p>
          <button 
            onClick={() => navigate('/company-registration')}
            className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            첫 기업 등록하기
          </button>
        </div>
      )}

      {/* 공개 설정 모달 */}
      {showVisibilityModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">공개 설정</h2>
              <button 
                onClick={() => setShowVisibilityModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                <strong>{selectedCompany.name}</strong>의 공개 범위를 설정합니다.
              </p>
              {visibilityOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleVisibilityChange(selectedCompany.id, option.value)}
                    className={`w-full flex items-center p-4 rounded-lg border-2 transition-colors ${
                      selectedCompany.visibility === option.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className={`h-6 w-6 mr-4 ${
                      selectedCompany.visibility === option.value ? 'text-emerald-600' : 'text-gray-400'
                    }`} />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{option.label}</p>
                      <p className="text-sm text-gray-500">{option.desc}</p>
                    </div>
                    {selectedCompany.visibility === option.value && (
                      <Check className="h-5 w-5 text-emerald-600 ml-auto" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 담당자 관리 모달 */}
      {showMemberModal && selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">담당자 관리</h2>
              <button 
                onClick={() => setShowMemberModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-6">
                <strong>{selectedCompany.name}</strong>의 담당자를 관리합니다.
              </p>

              {/* 담당자 초대 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                  <UserPlus className="h-5 w-5 mr-2 text-emerald-600" />
                  담당자 초대
                </h3>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="이메일 주소"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="viewer">뷰어</option>
                    <option value="editor">편집자</option>
                    <option value="admin">관리자</option>
                  </select>
                  <button
                    onClick={handleInviteMember}
                    disabled={!inviteEmail}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    초대
                  </button>
                </div>
              </div>

              {/* 담당자 목록 */}
              <h3 className="font-medium text-gray-900 mb-3">담당자 목록</h3>
              <div className="space-y-3">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium">
                        {member.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {member.user_name}
                          {!member.is_accepted && (
                            <span className="ml-2 text-xs text-amber-600">(대기중)</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{member.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        member.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                        member.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                        member.role === 'editor' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {roleLabels[member.role]}
                      </span>
                      {member.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* 역할 설명 */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-2">역할별 권한</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li><strong>소유자:</strong> 모든 권한 + 담당자 관리 + 기업 삭제</li>
                  <li><strong>관리자:</strong> 정보 수정 + 포트폴리오 관리 + 담당자 초대</li>
                  <li><strong>편집자:</strong> 포트폴리오 등록/수정</li>
                  <li><strong>뷰어:</strong> 비공개 정보 조회만</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 기업 상세 모달 */}
      {selectedCompany && !showMemberModal && !showVisibilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                  {selectedCompany.name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-semibold text-gray-900">{selectedCompany.name}</h2>
                    {selectedCompany.isMyCompany && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                        내 기업
                      </span>
                    )}
                    {getVisibilityIcon(selectedCompany.visibility)}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeColor(selectedCompany.type)}`}>
                      {selectedCompany.type}
                    </span>
                    <span className="text-sm text-gray-500">{selectedCompany.industry}</span>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setSelectedCompany(null)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">기본 정보</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">대표자</p>
                      <p className="font-medium">{selectedCompany.ceo}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">설립일</p>
                      <p className="font-medium">{selectedCompany.established}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">직원 수</p>
                      <p className="font-medium">{selectedCompany.employees}명</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Briefcase className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">매출액</p>
                      <p className="font-medium">{selectedCompany.revenue}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 연락처 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">연락처</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <span>{selectedCompany.address}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-400" />
                    <span>{selectedCompany.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <span>{selectedCompany.email}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Globe className="h-5 w-5 text-gray-400" />
                    <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                      {selectedCompany.website}
                    </a>
                  </div>
                </div>
              </div>

              {/* 성과 통계 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">성과 통계</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-900">{selectedCompany.portfolioCount}</p>
                    <p className="text-sm text-gray-500">포트폴리오</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">{selectedCompany.matchedGrants}</p>
                    <p className="text-sm text-gray-500">매칭 공모사업</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedCompany.successRate}%</p>
                    <p className="text-sm text-gray-500">선정률</p>
                  </div>
                </div>
              </div>

              {/* 기업 소개 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">기업 소개</h3>
                <p className="text-gray-600">{selectedCompany.description}</p>
              </div>

              {/* 태그 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">전문 분야</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedCompany.tags.map((tag, index) => (
                    <span key={index} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              {selectedCompany.isMyCompany && (
                <>
                  <button 
                    onClick={() => {
                      setShowVisibilityModal(true);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Settings className="h-4 w-4 mr-2 inline" />
                    공개 설정
                  </button>
                  <button 
                    onClick={() => {
                      loadMembers(selectedCompany.id);
                      setShowMemberModal(true);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Users className="h-4 w-4 mr-2 inline" />
                    담당자 관리
                  </button>
                </>
              )}
              <button className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <Edit className="h-4 w-4 mr-2 inline" />
                수정
              </button>
              <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                공모사업 매칭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDataPool;

